import React from 'react';
import { SentenceApi } from '../api/SentenceApi';
import { IVocabularyWord } from '../api/VocabularyWordApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import { PinyinFieldKey, TextFieldKey, textToPinyinMap } from './useVocabularyWordPinyin';

type VocabularyWordEntry = {
    word: IVocabularyWord;
    pinyinInput: Record<PinyinFieldKey, string>;
    errors: Record<string, string | null>;
};

interface UseVocabularyPinyinListProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
    initialList?: IVocabularyWord[];
}
export function useVocabularyWordListPinyin({ history, location, setErrorMessage, initialList = [] }: UseVocabularyPinyinListProps) {
    const [entryList, setEntryList] = React.useState<VocabularyWordEntry[]>(
        initialList.map(word => ({
            word,
            pinyinInput: Object.fromEntries(
                (Object.values(textToPinyinMap) as PinyinFieldKey[]).map(k => [k, ''])
            ) as Record<PinyinFieldKey, string>,
            errors: {}
        }))
    );

    const entryListRef = React.useRef(entryList);
    React.useEffect(() => {
        entryListRef.current = entryList;
    }, [entryList]);

    const vocabularyWordList = React.useMemo(() => entryList.map(e => e.word), [entryList]);

    type PartialPinyinInput = Partial<Record<PinyinFieldKey, string>>;
    type PartialVocabularyWordInput = Partial<IVocabularyWord>;
    type UpdateEntryPatchInput = Partial<Omit<VocabularyWordEntry, 'word' | 'pinyinInput'> & { word: PartialVocabularyWordInput; pinyinInput: PartialPinyinInput }>;
    const updateEntry = React.useCallback((index: number, patch: UpdateEntryPatchInput) => {
        setEntryList(prev => {
            const updated = [...prev];
            if (!updated[index]) return prev;
            updated[index] = {
                ...updated[index],
                ...patch,
                word: { ...updated[index].word, ...(patch.word || {}) },
                pinyinInput: { ...updated[index].pinyinInput, ...(patch.pinyinInput || {}) },
                errors: { ...updated[index].errors, ...(patch.errors || {}) }
            };
            return updated;
        });
    }, []);

    const setEntryPinyin = React.useCallback((index: number, pinyinField: PinyinFieldKey, pinyinList: string[]) => {
        updateEntry(index, {
            word: {
                [pinyinField]: pinyinList
            },
            pinyinInput: {
                [pinyinField]: pinyinList.join(' ')
            },
            errors: {
                [pinyinField]: null
            }
        });
    }, [updateEntry]);

    const fetchNumericPinyinForVocabularyField = React.useCallback((index: number, word: IVocabularyWord, field: TextFieldKey) => {
        const originalText = word[field] || '';
        if (!originalText.trim()) return;

        const pinyinField = textToPinyinMap[field];
        SentenceApi.getTextPinyin(originalText)
            .then((response) => {
                const pinyinList = response.data.flat();
                setEntryPinyin(index, pinyinField, pinyinList);
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
                updateEntry(index, {
                    errors: {
                        [pinyinField]: 'Unable to fetch pinyin'
                    }
                });
            });
    }, [setEntryPinyin, setErrorMessage, history, location, updateEntry]);

    const setVocabularyWordValue = React.useCallback((index: number, key: keyof IVocabularyWord, value: any) => {
        updateEntry(index, {
            word: { [key]: value }
        });
    }, [updateEntry]);

    const regenerateStudentResponsePromptPinyin = React.useCallback((index: number, updatedWord: IVocabularyWord) => {
        const text = updatedWord.student_response_prompt;
        if (text && text.trim()) {
            SentenceApi.getTextPinyin(text)
                .then((response) => {
                    const pinyinList = response.data.flat()
                    setEntryPinyin(index, 'student_response_prompt_pinyin', pinyinList);
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                    updateEntry(index, {
                        errors: {
                            student_response_prompt_pinyin: 'Unable to fetch pinyin'
                        }
                    });
                });
        }
    }, [setEntryPinyin, setErrorMessage, history, location, updateEntry]);

    const validatePinyin = React.useCallback((index: number, pinyinField: PinyinFieldKey, pinyinList: string[], originalText: string) => {

        SentenceApi.isTextPinyinValid(originalText, pinyinList, true)
            .then((response) => {
                const isValid = response.data;
                updateEntry(index, {
                    errors: {
                        [pinyinField]: isValid ? null : 'Pinyin invalid or incorrect length'
                    }
                });
                if (pinyinField === 'word_pinyin') {
                    regenerateStudentResponsePromptPinyin(index, {
                        ...entryList[index].word,
                        [pinyinField]: pinyinList
                    });
                }
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
                updateEntry(index, {
                    errors: {
                        [pinyinField]: 'Unable to validate pinyin'
                    }
                });
            });
    }, [updateEntry, regenerateStudentResponsePromptPinyin, setErrorMessage, history, location, entryList]);

    const handleVocabularyWordTextChange = React.useCallback((index: number, field: keyof IVocabularyWord, value: string) => {
        updateEntry(index, { word: { [field]: value } });

        if (Object.prototype.hasOwnProperty.call(textToPinyinMap, field)) {
            const textField = field as TextFieldKey;
            SentenceApi.getTextPinyin(value)
                .then((response) => {
                    const pinyinList = response.data.flat();
                    setEntryPinyin(index, textToPinyinMap[textField], pinyinList);
                    if (field === 'word') {
                        regenerateStudentResponsePromptPinyin(index, {
                            ...entryList[index].word,
                            [field]: value,
                            [textToPinyinMap[textField]]: pinyinList
                        });
                    }
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                    updateEntry(index, {
                        errors: {
                            [textToPinyinMap[textField]]: 'Unable to fetch pinyin'
                        }
                    });
                });
        }
    }, [updateEntry, setEntryPinyin, regenerateStudentResponsePromptPinyin, setErrorMessage, history, location, entryList]);

    const handleVocabularyWordPinyinChange = React.useCallback((index: number, pinyinField: PinyinFieldKey, value: string) => {
        const pinyinList = value.trim().split(/\s+/).filter(Boolean);
        updateEntry(index, {
            word: { [pinyinField]: pinyinList },
            pinyinInput: { [pinyinField]: value }
        });
        const originalField = (Object.entries(textToPinyinMap).find(([_, v]) => v === pinyinField) || [])[0] as TextFieldKey;
        const originalText = entryList[index].word[originalField] || '';
        validatePinyin(index, pinyinField, pinyinList, originalText)
    }, [updateEntry, validatePinyin, entryList]);

    const setVocabularyWordFields = React.useCallback((updatedWords: IVocabularyWord[]) => {
        const updatedEntries: VocabularyWordEntry[] = updatedWords.map((word, index) => {
            const pinyinInput: Record<PinyinFieldKey, string> = {} as Record<PinyinFieldKey, string>;
            (Object.keys(textToPinyinMap) as TextFieldKey[]).forEach((field) => {
                const pinyinField = textToPinyinMap[field];
                const originalText = word[field] || '';

                if (originalText.trim()) {
                    pinyinInput[pinyinField] = '';
                    fetchNumericPinyinForVocabularyField(index, word, field);
                } else {
                    pinyinInput[pinyinField] = '';
                }
            });

            return {
                word,
                pinyinInput,
                errors: {}
            };
        });

        setEntryList(updatedEntries);
    }, [fetchNumericPinyinForVocabularyField]);

    const setVocabularyWordListErrorMapPinyinMap = React.useCallback((updatedListOrUpdater: IVocabularyWord[] | ((prev: IVocabularyWord[]) => IVocabularyWord[])) => {
        const prevList = entryListRef.current.map(e => e.word);
        const updatedList = typeof updatedListOrUpdater === 'function' ? updatedListOrUpdater(prevList) : updatedListOrUpdater;
        setVocabularyWordFields(updatedList);
    }, [setVocabularyWordFields]);

    const clearVocabularyWordErrors = React.useCallback(() => {
        setEntryList(prev => prev.map(entry => ({ ...entry, errors: {} })));
    }, []);

    const getVocabularyWordErrorMap = React.useCallback((index: number, pinyinField: PinyinFieldKey) => entryList[index] ? entryList[index].errors[pinyinField] : null, [entryList]);
    const getVocabularyWordPinyinMap = React.useCallback((index: number, pinyinField: PinyinFieldKey) => entryList[index] ? entryList[index].pinyinInput[pinyinField] : "", [entryList]);
    const getVocabularyWordPinyinListMap = React.useCallback((index: number, pinyinField: PinyinFieldKey) => entryList[index] ? entryList[index].word[pinyinField] : [], [entryList]);

    return {
        vocabularyWordList,
        getVocabularyWordPinyinMap,
        getVocabularyWordPinyinListMap,
        getVocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordList: setVocabularyWordListErrorMapPinyinMap,
        setVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    };
}
